import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_import-hub-80/artifacts/r714uk97_BotBSlogo%20RGB.png";
const FLYER_HERO_FALLBACK = "https://customer-assets.emergentagent.com/job_import-hub-80/artifacts/owuwhzm5_IMG_1370.jpeg";

function MountainRidge({ flip = false, fill = "#0E2C46" }) {
  return (
    <svg
      viewBox="0 0 1400 110"
      preserveAspectRatio="none"
      className="block w-full"
      style={{ height: 56, transform: flip ? "scaleY(-1)" : "none" }}
      aria-hidden
    >
      <path
        d="M0,110 L0,80 L90,55 L180,75 L270,30 L370,70 L460,40 L560,80 L660,25 L760,65 L860,35 L960,75 L1060,30 L1170,70 L1270,40 L1360,75 L1400,55 L1400,110 Z"
        fill={fill}
      />
      <path
        d="M0,110 L0,95 L110,80 L230,90 L330,72 L450,90 L570,78 L690,92 L810,75 L930,90 L1050,80 L1190,92 L1290,82 L1400,90 L1400,110 Z"
        fill={fill}
        opacity="0.55"
      />
    </svg>
  );
}

export default function FlyerLegacyPage() {
  const [sale, setSale] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, set] = await Promise.all([
          axios.get(`${API}/sales/legacy`),
          axios.get(`${API}/site-settings`),
        ]);
        setSale(s.data);
        setSettings(set.data);
      } catch (e) {
        if (process.env.NODE_ENV === "development") console.error("Failed to load flyer data:", e);
      }
    })();
  }, []);

  const phone = settings?.contact_phone || "208-267-2668";
  const email = settings?.contact_email || "rymocattle@gmail.com";

  return (
    <div className="flyer-root" data-testid="flyer-legacy">
      {/* Toolbar — hidden in print */}
      <div className="flyer-toolbar no-print">
        <div className="flyer-toolbar-inner">
          <div>
            <div className="eyebrow" style={{ color: "var(--brass)" }}>Print-Ready Flyer</div>
            <div className="font-display text-2xl mt-1" style={{ color: "var(--brass-deep)" }}>Legacy Sale  •  December 5, 2026</div>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              data-testid="flyer-print-btn"
              onClick={() => window.print()}
              className="btn-primary"
            >
              Print / Save PDF
            </button>
            <a href="/sale/legacy" data-testid="flyer-back" className="btn-ghost">
              ← Back to Sale Page
            </a>
            <a href="/flyer/legacy/square" data-testid="flyer-ig-square" className="btn-ghost">
              IG Square
            </a>
            <a href="/flyer/legacy/story" data-testid="flyer-ig-story" className="btn-ghost">
              IG Story
            </a>
          </div>
        </div>
      </div>

      {/* Flyer page — 8.5 x 11 portrait */}
      <div className="flyer-page" id="flyer">
        {/* ===== Top brand band ===== */}
        <header className="flyer-brand">
          <img src={LOGO_URL} alt="Bulls of the Big Sky" className="flyer-logo" />
          <div className="flyer-brand-text">
            <div className="flyer-brand-name">Bulls of the Big Sky</div>
            <div className="flyer-brand-tag">SIMMENTAL  ★  ANGUS  ★  SIMANGUS</div>
            <div className="flyer-brand-script">Est. 2009  •  Built where the mountains meet the prairie.</div>
          </div>
        </header>

        <MountainRidge />

        {/* ===== Hero — image with title overlay ===== */}
        <section
          className="flyer-hero"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(14,44,70,0.30) 0%, rgba(14,44,70,0.55) 55%, rgba(14,44,70,0.92) 100%), url('${sale?.hero_image_url || FLYER_HERO_FALLBACK}')`,
          }}
        >
          <div className="flyer-hero-inner">
            <div className="flyer-hero-eyebrow">THE INAUGURAL LEGACY SALE</div>
            <h1 className="flyer-hero-title">Legacy of the Big&nbsp;Sky</h1>
            <div className="flyer-hero-date">December 5, 2026</div>
            <div className="flyer-hero-loc">Billings Livestock Commission  •  Billings, MT  •  1:00 PM MT</div>
          </div>
        </section>

        {/* ===== Stat grid — what to expect ===== */}
        <section className="flyer-stats">
          <div className="flyer-stat">
            <div className="flyer-stat-num">80</div>
            <div className="flyer-stat-label">Age-Advantaged Bulls</div>
            <div className="flyer-stat-sub">Fall-born &amp; yearling — ready to work</div>
          </div>
          <div className="flyer-stat">
            <div className="flyer-stat-num">100</div>
            <div className="flyer-stat-label">Commercial Bred Heifers</div>
            <div className="flyer-stat-sub">A strong female offering</div>
          </div>
          <div className="flyer-stat">
            <div className="flyer-stat-num">8</div>
            <div className="flyer-stat-label">Montana Programs</div>
            <div className="flyer-stat-sub">One catalog. One standard.</div>
          </div>
        </section>

        {/* ===== Body — sale details + sale day ===== */}
        <section className="flyer-body">
          <div className="flyer-body-col">
            <div className="flyer-h">About the Sale</div>
            <p className="flyer-p">
              The inaugural <strong>Bulls of the Big Sky Legacy Sale</strong> — held
              in conjunction with the <em>Western Choice Female Sale</em> at Billings Livestock
              Commission. Eighty age-advantaged bulls, fall-born and yearling, plus
              100 commercial bred heifers. One catalog,
              eight Montana programs, the bloodlines our buyers ask for by name.
            </p>
          </div>
          <div className="flyer-body-col flyer-body-col-side">
            <div className="flyer-h">Sale Day</div>
            <ul className="flyer-list">
              <li>
                <span className="flyer-list-when">SAT  DEC 5</span>
                <span className="flyer-list-what">Legacy of the Big Sky — 1:00 PM MT, in conjunction with the Western Choice Female Sale</span>
              </li>
              <li>
                <span className="flyer-list-when">VENUE</span>
                <span className="flyer-list-what">Billings Livestock Commission, Billings, MT</span>
              </li>
              <li>
                <span className="flyer-list-when">CATALOG</span>
                <span className="flyer-list-what">Full schedule, catalog &amp; lot videos to follow</span>
              </li>
            </ul>
          </div>
        </section>

        {/* ===== Featured bloodlines strip ===== */}
        <section className="flyer-blood">
          <div className="flyer-blood-label">FEATURED BLOODLINES</div>
          <div className="flyer-blood-names">
            WS&nbsp;Proclamation&nbsp;E202  ·  LBRS&nbsp;Genesis&nbsp;G69  ·  KBHR&nbsp;Global&nbsp;J138  ·  KBHR&nbsp;D103
          </div>
        </section>

        <MountainRidge />

        {/* ===== Footer — CTA + contact ===== */}
        <footer className="flyer-footer">
          <div className="flyer-footer-inner">
            <div className="flyer-cta">
              <div className="flyer-cta-eyebrow">2026 SALE BOOK</div>
              <div className="flyer-cta-title">Request a Catalog</div>
              <div className="flyer-cta-sub">Get the printed sale book mailed to your door.</div>
            </div>
            <div className="flyer-contact">
              <div className="flyer-contact-row">
                <span className="flyer-contact-key">CALL</span>
                <span className="flyer-contact-val">{phone}</span>
              </div>
              <div className="flyer-contact-row">
                <span className="flyer-contact-key">EMAIL</span>
                <span className="flyer-contact-val">{email}</span>
              </div>
              <div className="flyer-contact-row">
                <span className="flyer-contact-key">WEB</span>
                <span className="flyer-contact-val">bullsofthebigsky.com</span>
              </div>
            </div>
          </div>
          <div className="flyer-footer-strip">
            © {new Date().getFullYear()}  ·  BULLS OF THE BIG SKY  ·  SIMMENTAL ★ ANGUS ★ SIMANGUS
          </div>
        </footer>
      </div>

      <style>{`
        :root {
          --flyer-w: 8.5in;
          --flyer-h: 11in;
        }
        .flyer-root {
          background: #2d3a4a;
          min-height: 100vh;
          padding: 24px 0 64px;
        }
        .flyer-toolbar {
          max-width: 8.5in;
          margin: 0 auto 24px;
          background: var(--bg-primary);
          border: 1px solid var(--border-subtle);
          padding: 16px 20px;
        }
        .flyer-toolbar-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }

        /* ---- The page itself ---- */
        .flyer-page {
          width: var(--flyer-w);
          min-height: var(--flyer-h);
          margin: 0 auto;
          background: #F4EFE3;
          color: #13202F;
          font-family: 'Manrope', sans-serif;
          box-shadow: 0 24px 60px rgba(0,0,0,0.45);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          position: relative;
        }

        /* ---- Brand band ---- */
        .flyer-brand {
          background: #0E2C46;
          color: #FFFFFF;
          padding: 28px 40px 20px;
          display: flex;
          align-items: center;
          gap: 22px;
        }
        .flyer-logo {
          width: 92px;
          height: 92px;
          object-fit: contain;
          filter: drop-shadow(0 2px 6px rgba(0,0,0,0.4));
          flex-shrink: 0;
        }
        .flyer-brand-text { flex: 1; }
        .flyer-brand-name {
          font-family: 'Big Shoulders Display', sans-serif;
          font-weight: 800;
          font-size: 34px;
          line-height: 1;
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }
        .flyer-brand-tag {
          font-family: 'Big Shoulders Display', sans-serif;
          font-weight: 600;
          font-size: 11px;
          letter-spacing: 0.34em;
          color: #BFD4E0;
          margin-top: 8px;
        }
        .flyer-brand-script {
          font-family: 'Allura', cursive;
          font-size: 20px;
          color: #E8D7A8;
          margin-top: 6px;
          line-height: 1;
        }

        /* ---- Hero ---- */
        .flyer-hero {
          background-size: cover;
          background-position: center 55%;
          background-repeat: no-repeat;
          color: #FFFFFF;
          padding: 70px 40px 60px;
          position: relative;
        }
        .flyer-hero-inner { max-width: 100%; }
        .flyer-hero-eyebrow {
          font-family: 'Big Shoulders Display', sans-serif;
          font-weight: 700;
          font-size: 12px;
          letter-spacing: 0.36em;
          color: #E8D7A8;
          margin-bottom: 6px;
        }
        .flyer-hero-title {
          font-family: 'Big Shoulders Display', sans-serif;
          font-weight: 900;
          font-size: 128px;
          line-height: 0.88;
          letter-spacing: 0.005em;
          text-transform: uppercase;
          color: #FFFFFF;
          text-shadow: 0 4px 18px rgba(14,44,70,0.55);
          margin: 4px 0 12px;
        }
        .flyer-hero-date {
          font-family: 'Cormorant Garamond', serif;
          font-style: italic;
          font-weight: 500;
          font-size: 42px;
          color: #E8D7A8;
          line-height: 1;
          margin-bottom: 8px;
        }
        .flyer-hero-loc {
          font-family: 'Big Shoulders Display', sans-serif;
          font-weight: 600;
          font-size: 14px;
          letter-spacing: 0.24em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.92);
        }

        /* ---- Stats ---- */
        .flyer-stats {
          background: #F4EFE3;
          padding: 38px 40px 26px;
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 24px;
          border-bottom: 1px solid rgba(30,77,117,0.18);
        }
        .flyer-stat { text-align: left; }
        .flyer-stat-num {
          font-family: 'Big Shoulders Display', sans-serif;
          font-weight: 900;
          font-size: 92px;
          line-height: 0.85;
          color: #1E4D75;
          letter-spacing: -0.02em;
        }
        .flyer-stat-num-script {
          font-family: 'Allura', cursive;
          font-weight: 400;
          color: #C9A55F;
          font-size: 110px;
          line-height: 0.85;
        }
        .flyer-stat-label {
          font-family: 'Big Shoulders Display', sans-serif;
          font-weight: 800;
          font-size: 19px;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #0E2C46;
          margin-top: 6px;
          line-height: 1.05;
        }
        .flyer-stat-sub {
          font-size: 12px;
          color: #3F5266;
          margin-top: 4px;
          line-height: 1.35;
        }

        /* ---- Body ---- */
        .flyer-body {
          padding: 28px 40px 22px;
          display: grid;
          grid-template-columns: 1.35fr 1fr;
          gap: 36px;
          align-items: start;
        }
        .flyer-h {
          font-family: 'Big Shoulders Display', sans-serif;
          font-weight: 800;
          font-size: 13px;
          letter-spacing: 0.32em;
          color: #1E4D75;
          text-transform: uppercase;
          margin-bottom: 10px;
          border-bottom: 1.5px solid rgba(30,77,117,0.3);
          padding-bottom: 8px;
        }
        .flyer-p {
          font-size: 13.5px;
          line-height: 1.55;
          color: #13202F;
        }
        .flyer-p strong { color: #0E2C46; }
        .flyer-p em {
          font-family: 'Cormorant Garamond', serif;
          font-style: italic;
          font-weight: 600;
        }
        .flyer-list { list-style: none; padding: 0; margin: 0; }
        .flyer-list li {
          display: grid;
          grid-template-columns: 78px 1fr;
          gap: 12px;
          padding: 8px 0;
          border-bottom: 1px dashed rgba(30,77,117,0.22);
          align-items: baseline;
        }
        .flyer-list li:last-child { border-bottom: none; }
        .flyer-list-when {
          font-family: 'Big Shoulders Display', sans-serif;
          font-weight: 800;
          font-size: 12px;
          letter-spacing: 0.16em;
          color: #1E4D75;
          text-transform: uppercase;
          line-height: 1.2;
        }
        .flyer-list-what {
          font-size: 12.5px;
          color: #13202F;
          line-height: 1.4;
        }

        /* ---- Bloodlines strip ---- */
        .flyer-blood {
          margin: 4px 40px 26px;
          padding: 14px 18px;
          background: #ECE4D0;
          border-left: 4px solid #1E4D75;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .flyer-blood-label {
          font-family: 'Big Shoulders Display', sans-serif;
          font-weight: 800;
          font-size: 11px;
          letter-spacing: 0.34em;
          color: #1E4D75;
        }
        .flyer-blood-names {
          font-family: 'Cormorant Garamond', serif;
          font-style: italic;
          font-weight: 600;
          font-size: 17px;
          color: #0E2C46;
        }

        /* ---- Footer / CTA band ---- */
        .flyer-footer {
          margin-top: auto;
          background: #0E2C46;
          color: #FFFFFF;
        }
        .flyer-footer-inner {
          padding: 28px 40px 22px;
          display: grid;
          grid-template-columns: 1.3fr 1fr;
          gap: 24px;
          align-items: center;
        }
        .flyer-cta { }
        .flyer-cta-eyebrow {
          font-family: 'Big Shoulders Display', sans-serif;
          font-weight: 700;
          font-size: 11px;
          letter-spacing: 0.36em;
          color: #BFD4E0;
        }
        .flyer-cta-title {
          font-family: 'Big Shoulders Display', sans-serif;
          font-weight: 900;
          font-size: 52px;
          line-height: 0.95;
          text-transform: uppercase;
          letter-spacing: 0.005em;
          margin: 4px 0 6px;
        }
        .flyer-cta-sub {
          font-size: 13px;
          color: rgba(255,255,255,0.78);
        }
        .flyer-contact {
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(191,212,224,0.25);
          padding: 14px 18px;
        }
        .flyer-contact-row {
          display: grid;
          grid-template-columns: 58px 1fr;
          gap: 10px;
          align-items: baseline;
          padding: 5px 0;
        }
        .flyer-contact-row + .flyer-contact-row {
          border-top: 1px solid rgba(255,255,255,0.10);
        }
        .flyer-contact-key {
          font-family: 'Big Shoulders Display', sans-serif;
          font-weight: 800;
          font-size: 11px;
          letter-spacing: 0.28em;
          color: #BFD4E0;
        }
        .flyer-contact-val {
          font-size: 14px;
          color: #FFFFFF;
          font-weight: 500;
          letter-spacing: 0.01em;
          word-break: break-word;
        }
        .flyer-footer-strip {
          border-top: 1px solid rgba(255,255,255,0.12);
          padding: 9px 40px;
          font-family: 'Big Shoulders Display', sans-serif;
          font-weight: 700;
          font-size: 10px;
          letter-spacing: 0.34em;
          color: rgba(255,255,255,0.55);
          text-align: center;
        }

        /* ---- Print ---- */
        @media print {
          @page { size: Letter portrait; margin: 0; }
          html, body { background: #FFFFFF !important; }
          .flyer-root { background: #FFFFFF !important; padding: 0 !important; min-height: 0 !important; }
          .no-print { display: none !important; }
          .flyer-page {
            width: 8.5in !important;
            min-height: 11in !important;
            box-shadow: none !important;
            margin: 0 !important;
            page-break-after: avoid;
          }
        }
      `}</style>
    </div>
  );
}
